




























































import { Vue, Component, Ref, Watch } from 'vue-property-decorator'
import { getModule } from 'vuex-module-decorators'
import gsap, { Circ } from 'gsap'
import FeaturedTournament from '@/modules/tournament/components/FeaturedTournament'
import FeaturedTournamentMobile from '@/modules/tournament/components/FeaturedTournamentMobile'
import { unixTimestampToDateTime } from '@/utils'
import layoutStore from '@/modules/shared/layout.store'
import userStore from '@/modules/auth/auth.store'
import tournamentStore from '@/modules/tournament/tournament.store'
import ServerError from '@/utils/server_error'
import { TournamentEntity, TournamentState } from '@/api/tournament'
import { getRealtimeConnection, realtimeEventTarget } from '@/utils/realtime'
import { HubConnection } from '@microsoft/signalr'
import { CancelError, CancellationToken } from '@/utils/cancel'

@Component({
  components: {
    FeaturedTournament,
    FeaturedTournamentMobile
  }
})
export default class Landing extends Vue {
  layoutModule = getModule(layoutStore, this.$store)
  userModule = getModule(userStore, this.$store)
  tournamentModule = getModule(tournamentStore, this.$store)
  animating: boolean = false
  realtimeConnection!: HubConnection
  realtimeConnectionCancellationToken = new CancellationToken()
  isCatalogUpdatedToastVisible: boolean = false
  featuredTournamentIndex: number = -1
  isCarouselCycleTouched: boolean = false

  @Ref('featuredTournaments')
  readonly featuredTournaments!: FeaturedTournament[]

  @Ref('wrapper')
  readonly wrapper!: HTMLDivElement

  /* Getters */
  get tournaments() {
    return this.tournamentModule.tournaments.filter(
      tournament => tournament.isFeatured
    )
  }

  get hasLoggedIn() {
    return this.userModule.hasLoggedIn
  }

  carouselMove(upward: boolean) {
    if (upward) {
      this.featuredTournamentIndex++
    } else {
      this.featuredTournamentIndex--
    }
    this.isCarouselCycleTouched = true
  }

  async fetchTournamentCatalog() {
    try {
      await this.tournamentModule.setTournamentCatalogFilter() // reset filter
      await this.layoutModule.setProgressBarVisible(true)
      await this.tournamentModule.fetchTournamentCatalog()
    } catch (err) {
      if (err instanceof ServerError) {
        this.$toast.error(err.getMessage())
      } else {
        this.$toast.error(this.$t('Landing.SystemErrorMessage'))
        console.error(err)
      }
    } finally {
      await this.layoutModule.setProgressBarVisible(false)
    }
  }

  /* Hooks */
  async created() {
    await this.fetchTournamentCatalog()
    await this.subscribeRealtimeEvents()
    realtimeEventTarget.addEventListener(
      'connect',
      this.subscribeRealtimeEvents
    )
    realtimeEventTarget.addEventListener(
      'reconnected',
      this.subscribeRealtimeEvents
    )
  }

  beforeDestroy() {
    this.realtimeConnectionCancellationToken.cancel()
    this.unsubscribeRealtimeEvents()
    realtimeEventTarget.removeEventListener(
      'connect',
      this.subscribeRealtimeEvents
    )
    realtimeEventTarget.removeEventListener(
      'reconnected',
      this.subscribeRealtimeEvents
    )
    this.tournamentModule.setFeaturedTournamentIndex(-1)
  }

  /* Methods */
  unixToTime(value: number) {
    return unixTimestampToDateTime(value)
  }

  onTournamentSelected(
    featuredTournament: FeaturedTournament,
    tournament: TournamentEntity
  ) {
    this.animating = true
    this.tournamentModule.setSelectedTournament(tournament.tournamentId)

    this.$nextTick(() => {
      // get featured tournament wrapper div
      const element = featuredTournament.$el as HTMLDivElement

      // clone element (move banner position)
      const clonedElement = element.cloneNode(true) as HTMLDivElement
      clonedElement.style.position = 'absolute'
      clonedElement.style.left = element.offsetLeft + 'px'
      clonedElement.style.height = 'calc(100% - 3.75rem)'
      clonedElement.classList.remove('landing-featured-tournament')
      clonedElement.children[1].remove()
      clonedElement.classList.add('animating')
      this.wrapper.appendChild(clonedElement)

      // move banner
      gsap.to(clonedElement, {
        width: '75%',
        left: '25%',
        height: 'calc(100% - 0.75rem)',
        duration: 1
      })

      // clone element (logo to move position)
      const clonedLogoElement = element.children[1].cloneNode(
        true
      ) as HTMLDivElement
      clonedLogoElement.style.position = 'absolute'
      clonedLogoElement.style.display = 'block'
      clonedLogoElement.style.left = `calc(${100 / this.tournaments.length / 2 +
        this.tournaments.indexOf(tournament) *
          (100 / this.tournaments.length)}% - 120px)`
      clonedLogoElement.style.top = '2rem'
      clonedLogoElement.style.paddingTop = '0.75rem'
      clonedLogoElement.style.height = 'calc(100% - 3.75rem)'
      clonedLogoElement.style.zIndex = '1'
      this.wrapper.appendChild(clonedLogoElement)

      // dim all tournaments
      this.wrapper.style.backgroundColor = '#121212'
      gsap.to('.landing-featured-tournament div', {
        opacity: 0,
        duration: 0.5
      })

      // remove all tournament elements
      do {
        var childElements = document.getElementsByClassName(
          'landing-featured-tournament'
        ) as HTMLCollectionOf<Element>
        this.wrapper.removeChild(childElements[0])
      } while (childElements.length)

      // move logo
      gsap.to(clonedLogoElement, {
        left: this.hasLoggedIn ? '4.4rem' : '5rem',
        top: '2.75rem',
        duration: 1,
        onComplete: () => {
          this.navigateToTournament(tournament.tournamentId)
        }
      })
    })
  }

  onTournamentCatalogUpdated(tournamentId: string) {
    this.isCatalogUpdatedToastVisible = true
  }

  navigateToTournament(tournamentId: string) {
    this.$router.push({
      name: 'TournamentDetailsOverview',
      params: {
        tournamentId: tournamentId
      }
    })
  }

  @Watch('featuredTournamentIndex')
  async onCurrentFeaturedTournamentChanged(index: number) {
    await this.tournamentModule.setFeaturedTournamentIndex(index)
  }

  async reloadTournamentCatalog() {
    this.closeCatalogUpdatedToast()
    await this.tournamentModule.clearTournamentCatalog()
    await this.fetchTournamentCatalog()
  }

  closeCatalogUpdatedToast() {
    this.isCatalogUpdatedToastVisible = false
  }

  async subscribeRealtimeEvents() {
    try {
      this.realtimeConnection = await getRealtimeConnection(
        this.realtimeConnectionCancellationToken
      )
    } catch (err) {
      if (err instanceof CancelError) {
        return
      }

      this.$toast.error(this.$t('Landing.FailConnectServerMessage'))
      console.error(err)
    }
    this.realtimeConnection.on(
      'tournamentCatalogUpdated',
      this.onTournamentCatalogUpdated
    )
  }

  unsubscribeRealtimeEvents() {
    if (this.realtimeConnection) {
      this.realtimeConnection.off(
        'tournamentCatalogUpdated',
        this.onTournamentCatalogUpdated
      )
    }
  }
}
